import {createStore} from "@stencil/store";
import {ASSETS_FETCH_TYPE, IAsset, ICategory, STAGE, UserSettingsHandler} from "@trumpf/notifies.user-settings-handler";
import {IUserSettings, ICategories} from "@trumpf/notifies.user-settings-handler";

export const store = createStore<{
	userSettings: IUserSettings;
	categories: ICategory[];
	token: string | null;
	error: string | null;
	loading: boolean;
	unsavedChanges: boolean;
	saved: boolean;
	assets: IAsset[];
}>({
	userSettings: null,
	categories: null,
	token: null,
	error: null,
	loading: true,
	unsavedChanges: false,
	saved: false,
	assets: [],
});

const userSettingsHandler = new UserSettingsHandler();
// actions to execute after assets are received
const onAssetsReceivedToDos: {(): void}[] = [];

export function getUserSettings(token: string, locale: string, version: string, env: number, onDone?: () => void) {
	store.set("loading", true);
	getCategories(token, locale, version, env, () => {
		userSettingsHandler.getUserSettings(
			token,
			(receivedUserSettings: IUserSettings) => {
				store.set("userSettings", receivedUserSettings);
				store.set("loading", false);

				if (onDone) {
					onDone();
				}
			},
			async (response: Response) => {
				store.set("loading", false);
				store.set("error", `Error with status ${response.status}`);
			},
			version,
			env, // optional stage parameter
		);
	});
}

export function getCategories(token: string, locale: string, version: string, env: number, onDone?: () => void) {
	userSettingsHandler.getCategories(
		token,
		version,
		locale,
		(responseBody: ICategories) => {
			store.set("categories", responseBody.categories);
			if (onDone) {
				onDone();
			}
		},
		async (response: Response) => {
			store.set("loading", false);
			store.set("error", `Error with status ${response.status}`);
		},
		env, // optional stage parameter
	);
}

export function sendUserSettings(token: string, version: string, setting: IUserSettings, env: number, onDone?: () => void) {
	store.set("loading", true);
	userSettingsHandler
		.createUserSetting(token, version, setting, env)
		.then((response) => {
			store.set("loading", false);

			if (!response.ok) {
				throw new Error(`Error with status ${response.status}`);
			}

			store.set("unsavedChanges", false);
			store.set("saved", true);

			setTimeout(() => {
				store.set("saved", false);
			}, 3000);

			if (onDone) onDone();
		})
		.catch((err) => {
			store.set("loading", false);
			store.set("error", err.message);
		});
}

const onAssetsReceived = (totalCount: number, assets: IAsset[]) => {
	store.set("loading", false);
	store.set("error", null);

	//! this is just a workaround, the real problem why duplicate assets are received lies somewhere else
	//! probably in the life-cycle hooks of the component(s)

	// filter out assets that are already in the store
	const newAssets = assets.filter((newAsset) => !store.state.assets.some((oldAsset) => oldAsset.assetId === newAsset.assetId));

	store.set("assets", [...store.state.assets, ...newAssets]);
	// execute TODOs and clear them
	onAssetsReceivedToDos.forEach((toDo) => toDo());
	onAssetsReceivedToDos.length = 0;
};

const onAssetsNotOk = (response: Response) => {
	store.set("loading", false);
	store.set("error", `Error with status ${response.status}`);
};

const onNoNewAssets = () => {
	store.set("loading", false);
};

/**
 * @param noLimit if set, loads all remaining assets
 * @param onDone an optional callback to execute after loading more assets
 */
export function loadMoreAssets(noLimit?: boolean, onDone?: () => void) {
	userSettingsHandler.loadMoreAssets(noLimit);
	// push into TODOs after assets are received
	if (onDone) onAssetsReceivedToDos.push(onDone);
}

export function getInitialAssets() {
	userSettingsHandler.getAssets(ASSETS_FETCH_TYPE.initial);
}

export function subscribeAssets(token: string, version: string, stage: STAGE): () => void {
	store.set("loading", true);
	const unsubscribeAssetsCallback = userSettingsHandler.subscribeAssets({
		onAssetsReceived,
		onNotOk: onAssetsNotOk,
		onNoNewAssets,
		token,
		version,
		stage,
	});

	return unsubscribeAssetsCallback;
}

//! just a temp hack until assetFetching is consolidated
store.onChange("assets", () => loadMoreAssets());
